import { RESUME_SCREEN } from "@/lib/onboardingSteps";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { useAuth } from "@/hooks/useAuth";
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import Home from "@/pages/Home";
import { getAnyPushTarget } from "@/lib/pushNavigation";
import { isNativeApp } from "@/lib/platform";
import { supabase } from "@/integrations/supabase/client";
import {
  isOnboardingCompleteOnDevice,
  markOnboardingCompleteOnDevice,
} from "@/lib/onboardingFlag";

// Synchronous module-level guard: if a password-recovery redirect lands on `/`
// instead of `/reset-password`, forward immediately — BEFORE React mounts and
// before Supabase fires SIGNED_IN. The recovery token lives in the URL hash.
if (typeof window !== "undefined" && window.location.pathname === "/") {
  const hash = window.location.hash || "";
  const query = window.location.search || "";
  if (hash.includes("type=recovery") || query.includes("type=recovery")) {
    window.location.replace(`/reset-password${hash}${query}`);
  }
}

export const ACCOUNT_CHECK_TIMEOUT_MS = 2500;

/**
 * A push tap or universal link is pending when there is a stored or addressed
 * push target, or Despia already moved the address away from `/`. Every
 * signed in path below checks this before navigating or opening onboarding.
 */
function pushOrLinkPending(): boolean {
  return !!getAnyPushTarget() || window.location.pathname !== "/";
}

function releaseNativeHold() {
  try { document.documentElement.classList.remove("grounded-native-hold"); } catch { /* ignore */ }
}

type Decision =
  | { kind: "pending" }
  | { kind: "home" }
  | { kind: "onboarding"; initialScreen: number };

/**
 * Root route gate.
 *   Force params (?onboarding=1|true, ?resetOnboarding=1): QA entry, always onboarding.
 *   Signed in (app or browser): the "finished" check. Device flag set means
 *     Browse; otherwise the account record decides within 2.5s (unfinished
 *     resumes at the profile screen, error or timeout goes to Browse).
 *   Signed out: the iPhone app shows onboarding unless this device already saw
 *     it; browsers show onboarding for ?start=1 and the homepage otherwise.
 */
const Index = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { user, isLoading } = useAuth();
  const [decision, setDecision] = useState<Decision>({ kind: "pending" });
  const decidedFor = useRef<string | null>(null);

  const params = new URLSearchParams(search);
  const forceOnboarding =
    params.get("onboarding") === "1" ||
    params.get("onboarding") === "true" ||
    params.get("resetOnboarding") === "1";
  const wantsStart = params.get("start") === "1";
  // Native OAuth sign ins that happened mid onboarding return here so the
  // flow resumes at the profile screen instead of skipping to Browse.
  const authComplete = params.get("auth_complete") === "1";

  useEffect(() => {
    if (isLoading) return;

    const hash = window.location.hash || "";
    const query = window.location.search || "";
    if (hash.includes("type=recovery") || query.includes("type=recovery")) return;

    // Decide exactly once per visit to `/`. A sign up or sign in that happens
    // inside the onboarding flow changes the auth state while Index is still
    // mounted; the flow owns every step transition from there, so Index must
    // not re-run its front-door logic (no resumed event, no Browse redirect).
    if (decidedFor.current !== null) return;
    decidedFor.current = user?.id ?? "signed-out";

    if (forceOnboarding) {
      setDecision({ kind: "onboarding", initialScreen: 0 });
      return;
    }

    if (!user) {
      if (isNativeApp()) {
        const seen =
          isOnboardingCompleteOnDevice() ||
          localStorage.getItem("grounded_welcome_seen") === "true";
        if (seen) {
          // A push tap or universal link wins over the default Browse landing.
          if (!pushOrLinkPending()) navigate("/browse", { replace: true });
        } else {
          setDecision({ kind: "onboarding", initialScreen: 0 });
        }
        return;
      }
      setDecision(
        wantsStart
          ? { kind: "onboarding", initialScreen: 0 }
          : { kind: "home" },
      );
      return;
    }

    // Signed in. Each exit below uses the same push and address guard.
    const goBrowse = () => {
      if (!pushOrLinkPending()) navigate("/browse", { replace: true });
    };

    if (isOnboardingCompleteOnDevice()) {
      goBrowse();
      return;
    }

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      goBrowse();
    }, ACCOUNT_CHECK_TIMEOUT_MS);

    Promise.resolve(
      supabase
        .from("profiles")
        .select("onboarding_completed_at")
        .eq("id", user.id)
        .maybeSingle(),
    )
      .then(({ data, error }) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error || !data) {
          goBrowse();
          return;
        }
        if (data.onboarding_completed_at != null) {
          markOnboardingCompleteOnDevice();
          goBrowse();
          return;
        }
        if (pushOrLinkPending()) return;
        // A native OAuth sign in that completed mid onboarding arrives with
        // ?auth_complete=1: record the account step, not a resume.
        try {
          if (authComplete) {
            posthog.capture("onboarding_step_completed", { step: "auth" });
          } else {
            posthog.capture("onboarding_resumed", { screen: "profile" });
          }
        } catch { /* ignore */ }
        setDecision({ kind: "onboarding", initialScreen: RESUME_SCREEN });
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        goBrowse();
      });
    // Timer intentionally survives re-renders; `settled` guards double exits.
  }, [user, isLoading, navigate, forceOnboarding, wantsStart, authComplete]);

  useEffect(() => {
    if (decision.kind !== "pending") releaseNativeHold();
  }, [decision.kind]);
  useEffect(() => releaseNativeHold, []);

  if (decision.kind === "onboarding") {
    return (
      <OnboardingFlow initialScreen={decision.initialScreen} />
    );
  }
  if (decision.kind === "home") return <Home />;
  // Signed out browser visitors get the homepage on the very first render.
  if (!isLoading && !user && !forceOnboarding && !wantsStart && !isNativeApp()) return <Home />;
  // Blank hold while auth resolves and while the finished check runs.
  return null;
};

export default Index;
